import { pdfjs } from 'react-pdf';
import { PDFDocument } from 'pdf-lib';
import jsPDF from "jspdf";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

function base64ToUint8Array(base64) {
    const parts = base64.split(",");
    if (parts.length < 2) {
        return new Uint8Array([]);
    }
    const cleaned = parts[1].replace(/[^A-Za-z0-9+/=]/g, "").padEnd(parts[1].length + (4 - parts[1].length % 4) % 4, "=");
    try {
        const binary = atob(cleaned);
        return Uint8Array.from(binary, (char) => char.charCodeAt(0));
    } catch (error) {
        console.error("Base64 decoding error:", error);
        return new Uint8Array([]);
    }
}

export async function convertSvgUrlToBase64(svgString, maxWidth = 600, maxHeight = 400) {
    try {
        const response = await fetch(`https://blob.sgtradex.io/${svgString}`);
        if (!response.ok) throw new Error("Failed to fetch SVG");

        // Extract SVG text
        const svgText = await response.text();

        // Parse SVG for viewBox or width/height
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svgText, "image/svg+xml");
        const svgElem = svgDoc.documentElement;

        // Extract natural width and height
        let vbWidth = 300, vbHeight = 100; // fallback
        if (svgElem.hasAttribute("viewBox")) {
            const viewBox = svgElem.getAttribute("viewBox").split(" ");
            vbWidth = parseFloat(viewBox[2]);
            vbHeight = parseFloat(viewBox[3]);
        } else {
            vbWidth = parseFloat(svgElem.getAttribute("width")) || vbWidth;
            vbHeight = parseFloat(svgElem.getAttribute("height")) || vbHeight;
        }

        // Calculate scale to fit within maxWidth and maxHeight, preserving aspect ratio
        let scale = Math.min(maxWidth / vbWidth, maxHeight / vbHeight, 1); // Never scale up
        const outputWidth = Math.round(vbWidth * scale);
        const outputHeight = Math.round(vbHeight * scale);

        const img = new Image();
        img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgText)))}`;
        img.crossOrigin = "Anonymous";

        return new Promise((resolve, reject) => {
            img.onload = () => {
                const canvas = document.createElement("canvas");
                canvas.width = outputWidth;
                canvas.height = outputHeight;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, outputWidth, outputHeight);
                resolve(canvas.toDataURL("image/png"));
            };
            img.onerror = () => {
                reject("Error loading the SVG image.");
            };
        });

    } catch (error) {
        console.error("Error converting SVG URL to Base64:", error);
        return null;
    }
}

export class ProcessPDF {
    constructor(params) {
        this.staticHeader = params.staticHeader;
        this.orientation = params.orientation;
        this.format = params.format;
        this.marginX = params.marginX;
        this.marginTop = params.marginTop;
        this.marginBottom = params.marginBottom;
        this.fileName = params.fileName ?? 'document.pdf';
    }

    mergePdfs = async (bufferList, base64Image, ycoordinate) => {
        const mergedPdf = await PDFDocument.create();
        for (const buffer of bufferList) {
            const pdf = await PDFDocument.load(buffer);
            const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
            copiedPages.forEach((page) => mergedPdf.addPage(page));
        }
        const pages = mergedPdf.getPages();
        const totalPages = pages.length;

        // Converting Base64 to Uint8Array
        let imgBytes;
        let pngImage;
        if (base64Image) {
            imgBytes = base64ToUint8Array(base64Image);
            pngImage = await mergedPdf.embedPng(imgBytes);
        }

        pages.forEach((page, idx) => {
            const { width } = page.getSize();
            const text = `Page ${idx + 1} / ${totalPages}`;
            // Draw the PNG image near the lower right corner of the JPG image
            if (idx === 0 && base64Image && pngImage) {
                const { width: pageWidth } = page.getSize();
                const containerWidth = pageWidth * 0.5;
                const containerX = 0; /** First half starts at left edge */
                const imgWidth = pngImage.width ?? 75;
                const imgHeight = pngImage.height ?? 75;
                const imageX = containerX + (containerWidth * 0.6) - (imgWidth / 2);
                page.drawImage(pngImage, {
                    x: imageX,
                    y: ycoordinate,
                    width: imgWidth,
                    height: imgHeight,
                });
            }
            page.drawText(text, {
                x: Math.floor(width - (width / 2) - 20),
                y: 20,
                size: 14,
            });
        });
        return await mergedPdf.save();
    }

    createPdfImage = async (fileContent) => {
        const base64Images = await this.getImagesFromBase64Pdf(fileContent)
        const pdf = new jsPDF({ orientation: this.orientation, unit: "px", format: this.format, putOnlyUsedFonts: true });
        const pageWidth = pdf.internal.pageSize.getWidth();
        const marginTop = this.marginTop || 0;

        // Preload all images in parallel to get dimensions
        const imageMetas = await Promise.all(
            base64Images.map(base64 =>
                new Promise(resolve => {
                    const img = new Image();
                    img.onload = () => resolve({ base64, width: img.width, height: img.height });
                    img.src = base64;
                })
            )
        );

        imageMetas.forEach(({ base64, width, height }, index) => {
            const aspectRatio = (height - (this.marginTop + this.marginBottom)) / width;
            const calculatedHeight = pageWidth * aspectRatio;

            pdf.addImage(base64, 'PNG', 0, marginTop, pageWidth, calculatedHeight, undefined, 'FAST');

            if (this.staticHeader) this.staticHeader(pdf);
            if (index + 1 < imageMetas.length) pdf.addPage();
        });

        return pdf.output('arraybuffer');
    }

    getImagesFromBase64Pdf = async (base64) => {
        const pdfData = atob(base64.replace(/^data:application\/pdf;base64,/, ''));
        const loadingTask = pdfjs.getDocument({ data: pdfData });
        const pdf = await loadingTask.promise;

        const images = [];

        for (let i = 0; i < pdf.numPages; i++) {
            const page = await pdf.getPage(i + 1);
            const viewport = page.getViewport({ scale: 2 });

            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');

            canvas.height = viewport.height;
            canvas.width = viewport.width;

            await page.render({ canvasContext: context, viewport }).promise;

            images.push(canvas.toDataURL('image/png'));
        }

        return images;
    };

    generateArrayBuffer = async (htmlContent) => {
        const pdf = new jsPDF({ orientation: this.orientation, unit: "px", format: this.format, putOnlyUsedFonts: true });
        const pageWidth = pdf.internal.pageSize.getWidth();

        return new Promise((resolve) => {
            pdf.html(htmlContent, {
                callback: (pdf) => {
                    const totalPages = pdf.internal.getNumberOfPages();
                    for (let i = 1; i <= totalPages; i++) {
                        pdf.setPage(i);
                        if (this.staticHeader) this.staticHeader(pdf);
                    }

                    resolve(pdf.output("arraybuffer"));
                },
                margin: [
                    this.marginTop,
                    this.marginX,
                    this.marginBottom,
                    this.marginX,
                ],
                autoPaging: "text",
                width: pageWidth - this.marginX * 2,
                windowWidth: pageWidth - this.marginX * 2,
            });
        });

    }

    downloadPDF = async (content, base64Image, ycoordinate) => {
        try {
            let arrayOfBuffers = [];
            for (let item of content) {
                let result;
                if (item.type === "html") {
                    result = await this.generateArrayBuffer(item.data);
                } else {
                    result = await this.createPdfImage(item.data);
                }
                arrayOfBuffers.push(result);
            }

            const bytes = await this.mergePdfs(arrayOfBuffers, base64Image, ycoordinate);
            const blobUrl = URL.createObjectURL(
                new Blob([bytes], { type: "application/pdf" })
            );
            const link = Object.assign(document.createElement("a"), {
                href: blobUrl,
                download: this.fileName,
            });

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);

            return "PDF downloaded successfully";
        } catch (error) {
            return "Failed to download pdf";
        }
    };

}   